/**
 * Keeps a burst of API calls under the server's rate limit instead of colliding with it.
 *
 * Util/rateLimtConfig.js counts 30 requests per 60 seconds PER IP, in a FIXED window. A
 * page that uploads one file per row runs out of budget long before it runs out of rows,
 * and the 429 that follows loses the rest of the batch.
 *
 * take() lets the first `max` calls of a window straight through, then parks the rest
 * until the window rolls over — the caller gets a countdown to show meanwhile.
 *
 * The window is fixed and starts at the FIRST call, matching the server: because our
 * window can only start on or after the server's, a burst released after the wait always
 * lands in a later server window than the one before it.
 *
 * Create ONE pacer per server path and share it between every page that posts to it —
 * two pacers on the same path each think they own the whole budget.
 */
export const createRequestPacer = ({ max, windowMs = 60 * 1000 }) => {
  let windowStart = 0
  let used = 0

  return {
    /**
     * Reserve one request. Returns at once while budget is left; otherwise waits out the
     * window, calling onWait(secondsLeft) about once a second and onWait(0) when it is
     * free to go.
     */
    take: async (onWait) => {
      if (Date.now() - windowStart >= windowMs) {
        windowStart = Date.now()
        used = 0
      }
      if (used < max) {
        used += 1
        return
      }
      const until = windowStart + windowMs
      let left = until - Date.now()
      while (left > 0) {
        if (onWait) onWait(Math.ceil(left / 1000))
        // Copied per iteration: the timer callback must not close over the loop's `left`.
        const tick = Math.min(1000, left)
        await new Promise(resolve => setTimeout(resolve, tick))
        left = until - Date.now()
      }
      if (onWait) onWait(0)
      // This call opens the new window and is the first request in it.
      windowStart = Date.now()
      used = 1
    },

    /** Budget already spent elsewhere on the same path (a refresh the pacer cannot see). */
    spend: (n = 1) => {
      if (Date.now() - windowStart >= windowMs) {
        windowStart = Date.now()
        used = 0
      }
      used += n
    },
  }
}

export default createRequestPacer
